import { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, Chip, IconButton, SvgIcon, TextField, Tooltip } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import {
    fetchTenantAssistants,
    fetchTenantDefaultAssistants,
    replaceTenantDefaultAssistants
} from "../../../api/admin/tenant/endpoints-referrers";
import type {
    TenantAssistantOption,
    TenantDefaultAssistant
} from "../../../common/interfaces/admin/tenant.interface";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";

export interface TenantDefaultAssistantsProps {
    companyId: number;
    /** The (unsaved) toggle state — an empty list is refused while it is on. */
    autoProvisionEnabled: boolean;
    /** Whether the section is usable at all (needs an allowed-domains list). */
    domainsConfigured: boolean;
    /** Lets the tab gate the toggle on "at least one default Assistant". */
    onListChanged(list: TenantDefaultAssistant[]): void;
}

/**
 * Default Assistants list for automatic user creation (plan §2.4, Phase 5):
 * every automatically created user gets the `user` role on each row (D10) and
 * starts on the first. Rows save immediately through PUT (whole list), like
 * the SSO providers; only the toggle belongs to the tab's Save.
 */
export default function TenantDefaultAssistants({
    companyId,
    autoProvisionEnabled,
    domainsConfigured,
    onListChanged
}: TenantDefaultAssistantsProps) {
    const [list, setList] = useState<TenantDefaultAssistant[] | null>(null);
    const [assistants, setAssistants] = useState<TenantAssistantOption[]>([]);
    const [picked, setPicked] = useState<TenantAssistantOption[]>([]);
    const [error, setError] = useState<string>("");
    const [saving, setSaving] = useState(false);

    const publish = useCallback(
        (next: TenantDefaultAssistant[]) => {
            setList(next);
            onListChanged(next);
        },
        [onListChanged]
    );

    useEffect(() => {
        let cancelled = false;
        setList(null);
        setError("");
        Promise.all([fetchTenantDefaultAssistants(companyId), fetchTenantAssistants(companyId)])
            .then(([defaults, all]) => {
                if (cancelled) return;
                publish(defaults.data ?? []);
                setAssistants(all.data ?? []);
            })
            .catch(err => {
                if (!cancelled)
                    setError(err?.response?.data?.message ?? "Failed to load the Assistants");
            });
        return () => {
            cancelled = true;
        };
    }, [companyId, publish]);

    const save = async (uids: string[]) => {
        setSaving(true);
        setError("");
        try {
            const { data } = await replaceTenantDefaultAssistants(companyId, uids);
            publish(data ?? []);
            return true;
        } catch (err: any) {
            const raw = err?.response?.data?.message ?? "Saving the list failed";
            setError(Array.isArray(raw) ? raw.join(" ") : String(raw));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const currentUids = useMemo(() => (list ?? []).map(d => d.knowledgeBaseUid), [list]);
    const remaining = useMemo(
        () => assistants.filter(a => !currentUids.includes(a.uid)),
        [assistants, currentUids]
    );

    const add = async () => {
        if (!picked.length) return;
        if (await save([...currentUids, ...picked.map(p => p.uid)])) setPicked([]);
    };
    const move = (index: number, delta: number) => {
        const next = [...currentUids];
        const to = index + delta;
        if (to < 0 || to >= next.length) return;
        [next[index], next[to]] = [next[to], next[index]];
        void save(next);
    };
    const remove = (uid: string) => void save(currentUids.filter(u => u !== uid));

    const disabled = !domainsConfigured || saving;
    const lastWhileOn = autoProvisionEnabled && currentUids.length === 1;

    return (
        <div className="tenant-defaults">
            <div className="tenant-permissions__group-title" role="heading" aria-level={4}>
                Default Assistants
            </div>
            <p className="tenant-permissions__hint">
                Every automatically created user receives the <strong>User</strong> role on each
                Assistant below and starts in the first one. The list is read when the user is
                created; changing it later does not affect existing users. Changes here save
                immediately.
            </p>
            {!domainsConfigured && (
                <p className="tenant-permissions__hint">
                    Add at least one allowed domain above to set up automatic user creation.
                </p>
            )}
            {error && (
                <p className="tenant-permissions__hint" role="alert">
                    {error}
                </p>
            )}
            {autoProvisionEnabled && list && list.length === 0 && (
                <p className="tenant-permissions__hint tenant-defaults__warning" role="alert">
                    Automatic user creation is on but the list is empty (every listed Assistant has
                    been deleted); first logins are refused until an Assistant is added.
                </p>
            )}
            {list && list.length > 0 && (
                <ol className="tenant-defaults__list" aria-label="Default Assistants">
                    {list.map((row, index) => (
                        <li key={row.knowledgeBaseUid} className="tenant-defaults__row">
                            <span className="tenant-defaults__position">{index + 1}</span>
                            <span className="tenant-defaults__name">
                                {row.displayName || row.name}
                                {row.hidden && (
                                    <span className="tenant-defaults__hidden"> (hidden)</span>
                                )}
                            </span>
                            <Chip size="small" label="User" />
                            <Tooltip title="Move up">
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Move ${row.displayName || row.name} up`}
                                        disabled={disabled || index === 0}
                                        onClick={() => move(index, -1)}
                                    >
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Move down">
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Move ${row.displayName || row.name} down`}
                                        disabled={disabled || index === list.length - 1}
                                        onClick={() => move(index, 1)}
                                    >
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={
                                    lastWhileOn
                                        ? "Turn automatic user creation off before removing the last Assistant"
                                        : "Remove"
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Remove ${row.displayName || row.name}`}
                                        disabled={disabled || lastWhileOn}
                                        onClick={() => remove(row.knowledgeBaseUid)}
                                    >
                                        <SvgIcon
                                            component={TrashIcon}
                                            inheritViewBox
                                            fontSize="small"
                                        />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </li>
                    ))}
                </ol>
            )}
            {list && list.length === 0 && !autoProvisionEnabled && (
                <p className="tenant-permissions__hint">No default Assistants yet.</p>
            )}
            <div className="tenant-defaults__add">
                <Autocomplete
                    multiple
                    size="small"
                    className="tenant-defaults__picker"
                    options={remaining}
                    value={picked}
                    onChange={(_, v) => setPicked(v)}
                    disabled={disabled}
                    disableCloseOnSelect
                    getOptionLabel={a => a.displayName || a.name}
                    isOptionEqualToValue={(a, b) => a.uid === b.uid}
                    renderOption={(props, a) => (
                        <li {...props} key={a.uid}>
                            {a.displayName || a.name}
                            {a.hidden && <span className="tenant-defaults__hidden"> (hidden)</span>}
                        </li>
                    )}
                    renderInput={params => (
                        <TextField
                            {...params}
                            label="Add Assistant"
                            placeholder={remaining.length ? "Search…" : "All Assistants are listed"}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: params.InputProps.startAdornment
                            }}
                        />
                    )}
                />
                <Button variant="outlined" onClick={add} disabled={disabled || !picked.length}>
                    Add
                </Button>
            </div>
        </div>
    );
}
